import { useTheme } from "@mui/material/styles";
import * as chartUtils from "chartUtils";
import * as dates from "common/dates";
import * as numbers from "common/numbers";
import Component from "components/Component";
import * as labels from "config.labels";
import React from "react";
import { connect } from "react-redux";
import {
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  Cell,
  Text,
  ReferenceLine,
} from "recharts";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";
import * as utils from "utils";

export const formatToolTip = (value) => utils.sfString(value);

const CustomizedAxisTick = (props) => {
  const { x, y, payload } = props;
  return (
    <Text
      x={x}
      y={y}
      width={75}
      textAnchor={"middle"}
      fill={"#313d5e"}
      verticalAnchor={"start"}
    >
      {payload.value}
    </Text>
  );
};

const CustomToolTip = (props) => {
  const theme = useTheme();
  const { payload, formatter } = props;

  const tooltip = {
    backgroundColor: "white",
    opacity: "1",
    border: "1px solid #CECECE",
    borderRadius: "4px",
    paddingLeft: "20px",
    paddingRight: "20px",
  };

  return (
    <div>
      <div className={"custom-tooltip"} style={tooltip}>
        {(payload ?? []).map((item, i) => {
          return (
            <div key={i}>
              <p
                style={{
                  color:
                    item.payload.label === "UW Selected"
                      ? theme.brand.chart.secondary
                      : theme.brand.chart.primary,
                }}
                key={i}
              >
                {item.payload.label}
                {":"} <strong>{formatter(item.payload.value)}</strong>
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const LayerPricingChartComponent = (props) => {
  const theme = useTheme();
  const {
    chartRef,
    tooltipPosition,
  } = chartUtils.useTooltipPositionerFixingBottomRightToChartTopRight();

  const { targetPerspective, proRataFactor } = props;

  // Function to adjust chart data based on the current perspective
  const getAdjustedChartData = () => {
    if (!props.data || proRataFactor === 1) {
      return props.data;
    }

    // Apply similar adjustment logic as in PricingTable.js's adjustValuesByPerspective
    return props.data.map((item) => {
      const adjustedItem = { ...item };

      // Only adjust the value (which represents requiredPremium) when in pro-rated view
      if (
        targetPerspective === dates.ProratedPerspective.PRORATED &&
        typeof adjustedItem.value === "number"
      ) {
        // Convert from annualized to pro-rated
        adjustedItem.value = adjustedItem.value * proRataFactor;
      }

      return adjustedItem;
    });
  };
  const isVisible = ({ byTag, defaultVisible = false }) => {
    return props.userConfig.componentVisibility?.[byTag] ?? defaultVisible;
  };

  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const percentTickFormatter = (value) => {
    return !props.layer.grossPremium
      ? ""
      : (
          (100 - props.layer.profit - props.layer.expenses) *
          (value / props.layer.grossPremium)
        ).toFixed(0) + "%";
  };

  const toolTipFormatter = (value) => {
    // No need to adjust for perspective here - the data is already adjusted in getAdjustedChartData()
    return (
      "$" +
      value.toLocaleString(undefined, {
        maximumFractionDigits: 0,
      })
    );
  };

  const percentToolTipFormatter = (value) => {
    return (
      (value * 100).toLocaleString(undefined, {
        maximumFractionDigits: 3,
      }) + "%"
    );
  };

  return (
    <Component
      title={translateLabel(labels.LAYER_PRICING_CHART_TITLE, {
        default: "Required Premium",
      })}
      subtitle={translateLabel(labels.LAYER_PRICING_CHART_SUBTITLE, {
        default:
          "The premium required to cover the expected loss cost computed via different pricing methods.",
      })}
    >
      <div
        style={{
          position: "relative",
          width: "100%",
          paddingBottom: "500px",
        }}
      >
        <div
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: 0,
            top: 0,
          }}
        >
          <ResponsiveContainer width={"100%"} height={"100%"}>
            <BarChart
              width={500}
              height={500}
              data={getAdjustedChartData()}
              margin={{
                top: 20,
                right: 30,
                left: 20,
                bottom: 20,
              }}
              ref={chartRef}
            >
              <CartesianGrid strokeDasharray={"3 3"} />
              <XAxis
                dataKey={"label"}
                interval={0}
                angle={-45}
                textAnchor={"end"}
                tick={<CustomizedAxisTick />}
                height={80}
              />
              {isVisible({
                byTag: "pricing.layerPricing.chart.purePremium",
                defaultVisible: true,
              }) && (
                <YAxis
                  label={{
                    value: translateLabel(
                      labels.LAYER_PRICING_CHART_Y_AXIS_LABEL,
                      {
                        default: "Required 100% Premium",
                      }
                    ),
                    angle: -90,
                    position: "left",
                    style: { textAnchor: "middle" },
                    fill: "#313d5e",
                  }}
                  domain={[() => 0, () => props.chartMaximum]}
                  tickFormatter={chartUtils.tickFormatter}
                />
              )}
              {isVisible({
                byTag: "pricing.layerPricing.chart.purePremiumPercent",
                defaultVisible: false,
              }) && (
                <YAxis
                  label={{
                    value: "Pure Premium",
                    angle: -90,
                    position: "left",
                    style: { textAnchor: "middle" },
                    fill: "#313d5e",
                  }}
                  domain={[() => 0, () => props.chartMaximum]}
                  tickFormatter={(x) =>
                    chartUtils.tickFormatter(numbers.toPercentFromDecimal(x)) +
                    "%"
                  }
                />
              )}
              {isVisible({
                byTag: "pricing.layerPricing.chart.GNULR",
                defaultVisible: true,
              }) && (
                <YAxis
                  label={{
                    value: translateLabel(
                      labels.LAYER_PRICING_CHART_GNULR_LABEL,
                      {
                        default: "GNULR",
                      }
                    ),
                    angle: 90,
                    position: "right",
                    style: { textAnchor: "middle" },
                    fill: "#313d5e",
                  }}
                  yAxisId={1}
                  domain={[() => 0, () => props.chartMaximum]}
                  tickFormatter={percentTickFormatter}
                  orientation={"right"}
                />
              )}
              {isVisible({
                byTag: "pricing.layerPricing.chart.percentTooltip",
                defaultVisible: false,
              }) && (
                <Tooltip
                  position={tooltipPosition}
                  isAnimationActive={false}
                  content={({ payload, label }) => (
                    <CustomToolTip
                      payload={payload}
                      label={label}
                      formatter={percentToolTipFormatter}
                    />
                  )}
                />
              )}
              {isVisible({
                byTag: "pricing.layerPricing.chart.dollarTooltip",
                defaultVisible: true,
              }) && (
                <Tooltip
                  position={tooltipPosition}
                  isAnimationActive={false}
                  content={({ payload, label }) => (
                    <CustomToolTip
                      payload={payload}
                      label={label}
                      formatter={toolTipFormatter}
                    />
                  )}
                />
              )}
              <Legend />
              <Bar
                dataKey={"value"}
                fill={"#82ca9d"}
                legendType={"none"}
                name={"Gross Premium"}
              >
                {props.data.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={
                      index === props.data.length - 1
                        ? theme.brand.chart.secondary
                        : theme.brand.chart.primary
                    }
                  />
                ))}
              </Bar>
              {isVisible({
                byTag: "pricing.layerPricing.chart.grossPremium",
                defaultVisible: true,
              }) && (
                <ReferenceLine
                  y={
                    targetPerspective === dates.ProratedPerspective.ANNUALIZED
                      ? proRataFactor
                        ? props.layer.grossPremium / proRataFactor
                        : null
                      : props.layer.grossPremium
                  }
                  stroke={theme.brand.black}
                  label={{
                    value: `100% Bound Premium - $${chartUtils.tickFormatter(
                      targetPerspective === dates.ProratedPerspective.ANNUALIZED
                        ? proRataFactor
                          ? props.layer.grossPremium / proRataFactor
                          : null
                        : props.layer.grossPremium
                    )}`,
                    position: "insideBottom",
                    fill: theme.brand.black,
                  }}
                  strokeWidth={4}
                  ifOverflow={"extendDomain"}
                />
              )}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Component>
  );
};

const mapDispatchToProps = {};

export const mapStateToProps = (state) => {
  const data = pricingSelectors.formatGrossPremiumChartData(state);
  const userConfig = userSelectors.selectUserConfig(state);

  const includeGrossPremiumOnChart =
    userConfig.componentVisibility?.[
      "pricing.layerPricing.chart.grossPremium"
    ] ?? true;

  return {
    data,
    layer: pricingSelectors.getActiveLayer(state) ?? {},
    chartMaximum: includeGrossPremiumOnChart
      ? pricingSelectors.getChartMaximum(state)
      : numbers.roundUpNice(Math.max(5e-6, ...data.map((x) => x.value))),
    userConfig,
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LayerPricingChartComponent);
